import { Badge } from '@artic/ui/badge';
import { createFileRoute } from '@tanstack/react-router';

import { OperationDangerZone } from '../../features/operations/operation-danger-zone';
import { OperationDetailShell } from '../../features/operations/operation-detail-shell';
import { OperationForm } from '../../features/operations/operation-form';
import { operationDetailQueryOptions } from '../../features/operations/queries';

export const Route = createFileRoute('/operations/$operation_id')({
  staticData: {
    // Resolve the operation name from the cache so the breadcrumb reads
    // "Operations > {name}" instead of "Operations > Operation". Returns
    // null until the detail query has populated, which the breadcrumbs
    // component treats as "render no leaf yet".
    breadcrumb: (match, queryClient) => {
      const { operation_id: operationId } = match.params as { operation_id: string };
      const data = queryClient.getQueryData(operationDetailQueryOptions(operationId).queryKey);
      return data?.operation_name ?? null;
    },
    // ENG-624: the version (and Archived state, when set) live in the
    // breadcrumbs row instead of repeating the name in a form header.
    breadcrumbBadges: (match, queryClient) => {
      const { operation_id: operationId } = match.params as { operation_id: string };
      const data = queryClient.getQueryData(operationDetailQueryOptions(operationId).queryKey);
      if (!data) return null;
      return (
        <>
          <Badge variant="outline" className="font-mono text-xs">
            v{data.operation_version}
          </Badge>
          {data.is_archived && <Badge variant="secondary">Archived</Badge>}
        </>
      );
    },
    layout: 'full-height',
  },
  component: OperationDetailsPage,
});

function OperationDetailsPage() {
  const { operation_id: operationId } = Route.useParams();

  return (
    <OperationDetailShell operationId={operationId} active="details">
      {(operation, partnerId) => (
        // min-h-full + mt-auto keeps the Danger Zone flushed to the bottom of
        // the page regardless of how short the form is.
        <div className="flex min-h-full flex-col gap-6 p-6">
          <OperationForm operation={operation} partnerId={partnerId} />
          <div className="mt-auto pt-6">
            <OperationDangerZone operation={operation} partnerId={partnerId} />
          </div>
        </div>
      )}
    </OperationDetailShell>
  );
}
